import { types, flow, getRoot } from 'mobx-state-tree'
import Router from 'next/router'
import io from 'socket.io-client'

import buildCustomValidationStore from '~/stores/models/customValidation'
import { FETCHER_VALIDATIONS, FETCHER_ASYNC_VALIDATIONS } from './lookupConfig'
import DivorceFetcherCase from './models/DivorceFetcherCase'
import TRACKING from '~/services/tracking'
import * as Delays from '~/utilities/delays'
import objectIsEmpty from '~/utilities/objectIsEmpty'
import { REQUEST_STATUSES, FETCHER_TIMEOUT_DURATIONS } from '~/utilities/constants'

const DivorceLookupStore = types
  .model('DivorceLookupStore', {
    firstName: '',
    middleName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    fetcherState: types.optional(types.string, ''),
    fetcherName: types.optional(types.string, ''),
    fetcherCases: types.array(DivorceFetcherCase),
    fetcherStatus: types.optional(
      types.enumeration('FetcherStatus', Object.keys(REQUEST_STATUSES)),
      REQUEST_STATUSES.init,
    ),
  })
  .views((self) => ({
    get searchData() {
      const { auth } = getRoot(self)

      const hasUtmParams = !objectIsEmpty(auth.utmParameters)

      return {
        contactInfo: {
          fullName: `${self.firstName} ${self.middleName} ${self.lastName}`,
          utm: hasUtmParams ? auth.utmParameters : auth.user.utm,
          phoneNumber: self.phoneNumber,
          email: self.email,
        },
        firstName: self.firstName,
        middleName: self.middleName,
        lastName: self.lastName,
        fetcherState: self.fetcherState,
        fetcherName: self.fetcherName,

        telemetry: {
          origin_create: 'v2 web app divorce lookup flow',
        },
      }
    },
  }))
  .actions((self) => ({
    validateForm: flow(function* validateForm() {
      self.resetTouched()
      self.resetDisplayErrors()

      self.validateKeyList(['firstName', 'middleName', 'lastName', 'phoneNumber', 'email'])
      yield self.asyncValidateKeyList(['phoneNumber'])
    }),
    setFetcherName(fetcherName) {
      self.fetcherName = fetcherName
    },
    setFetcherState(fetcherState) {
      self.fetcherState = fetcherState
    },
    setFetcherStatus(status) {
      self.fetcherStatus = status
    },
    setFetcherCases(fetcherCases) {
      self.fetcherCases = fetcherCases
    },
    showResultsPage() {
      Router.push('/divorce/lookup/results')
    },
    getFetcherCases: flow(function* getFetcherCases() {
      yield self.validateForm()
      if (self.hasDisplayErrors) return

      // Google analytics timer
      const searchTimer = TRACKING.divorce.fetcherResultsTiming('Lookup informations added')
      searchTimer.start()

      // Timeout for fetcher request
      const timeoutEnabled = process.env.ENABLE_DIVORCE_FETCHER_TIMEOUT_DURATION === 'true'

      const timer = setTimeout(() => {
        if (timeoutEnabled) {
          console.info('Fetcher response took too long')
          self.showResultsPage()
        }
      }, FETCHER_TIMEOUT_DURATIONS.divorce[self.fetcherName] || FETCHER_TIMEOUT_DURATIONS.divorce.DEFAULT)

      try {
        self.setFetcherStatus(REQUEST_STATUSES.loading)

        clearTimeout(timer)

        const socketUrl = process.env.PARSE_SERV_URL
        const socket = io(socketUrl, { transports: ['websocket'] })

        socket.on('connect', () => {
          socket.emit('getFetcherCases', self.searchData)

          socket.on('getFetcherCasesError', (error) => {
            clearTimeout(timer)

            self.setFetcherStatus(REQUEST_STATUSES.error)
            console.error('Error fetching cases (socket event): ', error)
          })

          socket.on('getFetcherCasesResults', (fetcherCases) => {
            clearTimeout(timer)

            try {
              self.setFetcherCases(fetcherCases.map(({ fetcherDivorceCase }) => fetcherDivorceCase))

              self.showResultsPage()
              
              if (fetcherCases.length > 0) {
                TRACKING.divorce.fetcherResults()
              }

              searchTimer.finish()
              Delays.msDelayPromise(1500).then(() => {
                self.setFetcherStatus(REQUEST_STATUSES.complete)
              })
            } catch (error) {
              self.setFetcherStatus(REQUEST_STATUSES.error)
              console.error('Error fetching cases (showing results): ', error)
            }
          })
        })
      } catch (error) {
        clearTimeout(timer)

        self.setFetcherStatus(REQUEST_STATUSES.error)
        console.error('Error fetching cases (general error): ', error)
      }
    }),
  }))

const CustomValidation = buildCustomValidationStore({
  validationObject: FETCHER_VALIDATIONS,
  asyncValidationObject: FETCHER_ASYNC_VALIDATIONS,
})

const DivorceFetcherModel = types.compose(DivorceLookupStore, CustomValidation)

export default DivorceFetcherModel
